<template>
  <segment>
    <h3 class="ui dividing header">
      <i class="clock outline icon"/>
      <div class="content">
        {{ $t('patients.appointments.new') }}
      </div>
    </h3>
    <div class="ui stackable grid">
      <div class="ten wide column">
        <div
          v-if="showReminderSettingsWarning"
          class="ui icon info message">
          <i class="warning circle icon"/>
          <div class="content">
            <div class="header">
              {{ $t('patients.appointments.reminderSettingsNotSet') }}
            </div>
            <ul>
              <template v-for="(settingValue, settingKey) in reminderSettings">
                <li
                  v-if="!settingValue"
                  :key="settingKey">
                  {{ $t(`patients.appointments.${settingKey}NotSet`) }}
                </li>
              </template>
            </ul>
          </div>
        </div>
        <!-- <standalone-patient-language-setting-->
        <!--   v-if="showStandalonePatientLanguageSetting"-->
        <!--   @selected-patient-language="patientLang = $event"/>-->
        <message :message="formMessage"/>
        <form
          @submit.prevent="newAppointment"
          class="ui form">
          <component
            v-for="(field, key) in NewAppointmentForm"
            :key="key"
            :is="field.component"
            :data="field.data"
            :class="{ disabled: !patientLang || (hasOneUpcomingCoachAppointment && hasOneUpcomingDoctorAppointment) }"
            v-model="scheduleAppointment[key]"/>
          <button
            :disabled="isLoading || hasMissingFields"
            :class="{ loading: isLoading }"
            class="ui labeled icon primary button"
            type="submit">
            <i class="plus icon"/>
            Schedule Appointment
          </button>
        </form>
      </div>
    </div>
    <br>

    <patient-appointments-upcoming
      @updated="updateBraze"
      :patient="patient"
      :appointments="appointments.upcoming"/>

    <patient-appointments-past
      @updated="$emit('refresh')"
      :appointments="appointments.past"
      :patient="patient"/>
  </segment>
</template>

<script>
import { Patient, Appointment, DraftAppointment } from '../../structs/structs'
import { mapState } from 'vuex'
import PatientAppointmentsUpcoming from './PatientAppointmentsUpcoming'

import { NewAppointmentForm } from '../../forms/forms'
import Segment from '../utility/Segment'
import PatientAppointmentsPast from './PatientAppointmentsPast'
// import StandalonePatientLanguageSetting from './StandalonePatientLanguageSetting'

export default {
  name: 'PatientAppointments',
  props: {
    patient: {
      type: Patient,
      required: true
    }
  },
  components: {
    PatientAppointmentsPast,
    // StandalonePatientLanguageSetting,
    Segment,
    PatientAppointmentsUpcoming
  },
  data () {
    return {
      // patientLang: this.patient.data.patientReminderSettings.patientLang,
      patientLang: null,
      NewAppointmentForm,
      scheduleAppointment: {
        data: {}
      },
      isLoading: false,
      formMessage: {
        text: '',
        class: ''
      },
      calendarMessage: {
        text: '',
        class: ''
      }
    }
  },
  computed: {
    ...mapState(['userId']),

    appointments () {
      let upcoming = []
      let past = []

      let appointments = (
        this.patient.userEvents
          .filter(event => event.type === 'appointment')
          .map(appointment => new Appointment(appointment))
      )

      appointments.forEach(n => {
        n.startTimestamp > Date.now() ? upcoming.push(n) : past.push(n)
      })

      upcoming.sort((a, b) => a.startTimestamp - b.startTimestamp)
      past.sort((a, b) => b.startTimestamp - a.startTimestamp)

      return {
        upcoming,
        past
      }
    },
    upcomingCoachAppointment () {
      return (
        this.appointments.upcoming
          .filter(appointment => appointment.data.appointmentType === 'coach')
          .find(appointment => !appointment.data.isCanceled && !appointment.data.isRescheduled)
      ) || null
    },
    upcomingDoctorAppointment () {
      return (
        this.appointments.upcoming
          .filter(appointment => appointment.data.appointmentType === 'doctor')
          .find(appointment => !appointment.data.isCanceled && !appointment.data.isRescheduled)
      ) || null
    },
    hasOneUpcomingCoachAppointment () {
      return !!this.upcomingCoachAppointment
    },
    hasOneUpcomingDoctorAppointment () {
      return !!this.upcomingDoctorAppointment
    },
    hasMissingFields () {
      return this.checkMissingFields(this.NewAppointmentForm, this.scheduleAppointment)
    },
    reminderSettings () {
      let { patientLang, shouldSendCoachReminder, shouldSendDocReminder } = this.patient.data.patientReminderSettings

      return {
        patientLang,
        shouldSendCoachReminder,
        shouldSendDocReminder
      }
    },
    showReminderSettingsWarning () {
      return Object.values(this.reminderSettings).some(setting => !setting)
    }
    // showStandalonePatientLanguageSetting () {
    //   let bothAppointmentsScheduled = this.hasOneUpcomingCoachAppointment && this.hasOneUpcomingDoctorAppointment
    //   let hasPatientLang = !!this.patient.data.patientReminderSettings.patientLang
    //
    //   // Hide standalone patient language setting as long as it has been set or both appointment types were set
    //   return (
    //     !hasPatientLang
    //       ? !bothAppointmentsScheduled
    //       : false
    //   )
    // }
  },
  watch: {
    hasOneUpcomingCoachAppointment: {
      handler (newValue) {
        let appointmentTypeOptions = this.NewAppointmentForm.data.data.appointmentType.data.options
        let coachOption = appointmentTypeOptions.find(option => option.value === 'coach')
        coachOption.disabled = newValue
      },
      immediate: true
    },
    hasOneUpcomingDoctorAppointment: {
      handler (newValue) {
        let appointmentTypeOptions = this.NewAppointmentForm.data.data.appointmentType.data.options
        let doctorOption = appointmentTypeOptions.find(option => option.value === 'doctor')
        doctorOption.disabled = newValue
      },
      immediate: true
    },
    'patient.data.patientReminderSettings.shouldSendCoachReminder': {
      handler (newValue) {
        if (newValue && this.scheduleAppointment.data.appointmentType === 'coach') {
          this.scheduleAppointment.data.hasReminder = newValue
        }
      },
      immediate: true
    },
    'patient.data.patientReminderSettings.shouldSendDocReminder': {
      handler (newValue) {
        if (newValue && this.scheduleAppointment.data.appointmentType === 'doctor') {
          this.scheduleAppointment.data.hasReminder = newValue
        }
      },
      immediate: true
    },
    'patient.data.patientReminderSettings.patientLang': {
      handler (newValue) {
        if (newValue) {
          this.patientLang = newValue
        }
      },
      immediate: true
    }
  },
  methods: {
    newAppointment () {
      this.isLoading = true
      this.formMessage = { text: '', class: '' }
      let newAppointment = new DraftAppointment(
        {
          type: 'appointment',
          startTimestamp: this.scheduleAppointment.date + this.scheduleAppointment.time,
          endTimestamp: null,
          data: {
            appointmentType: this.scheduleAppointment.data.appointmentType,
            rescheduledAppointmentId: null,
            isRescheduled: false,
            toReschedule: false,
            isCanceled: false,
            hasReminder: this.scheduleAppointment.data.hasReminder
          }
        }
      )
      let body = [newAppointment]

      let requestUrl = `${process.env.VUE_APP_PROGRAM_URL}/programUsers/${this.patient.userId}/addUserEvents`

      this.$axios.post(
        requestUrl,
        body,
        this.requestConfig
      ).then(
        () => {
          this.scheduleBrazeAppointmentReminder(newAppointment)
            .then(
              () => {
                this.$emit('refresh')

                this.formMessage = {
                  class: 'success',
                  text: this.$t('patients.appointments.scheduledSuccess')
                }

                this.scheduleAppointment = {
                  data: {}
                }

                // if (body[0].data.hasReminder) {
                //   //
                //   this.updateBraze(body.startTimestamp)
                // }
              }
            )
        },
        error => {
          this.$rollbar.error(error)
          this.formMessage = {
            text: this.$t('errors.unexpected'),
            class: 'error'
          }
        }
      ).finally(
        () => {
          this.isLoading = false
        }
      )
    },
    updateBraze (appointment) {
      this.scheduleBrazeAppointmentReminder(appointment)
        .then(
          () => { this.$emit('refresh') }
        )
    },
    constructBrazeAppointmentReminder (appointment) {
      let shouldSendReminderBrazeAttributes = new Map(
        [
          ['doctor', '3EShouldSendDocReminder'],
          ['coach', '3EShouldSendCoachReminder']
        ]
      )
      let shouldSendReminderSettingAttributes = new Map(
        [
          ['doctor', 'shouldSendDocReminder'],
          ['coach', 'shouldSendCoachReminder']
        ]
      )
      let appointmentDateTimeFormat = {
        en: { timeZone: 'UTC', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true },
        de: { timeZone: 'UTC', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', hour12: false }
      }

      let language = this.patient.data.patientReminderSettings.patientLang
      let appointmentType = appointment.data.appointmentType
      let shouldSendReminderKey = shouldSendReminderBrazeAttributes.get(appointmentType)
      let shouldSendReminderValue = appointment.data.hasReminder || this.patient.data.patientReminderSettings[shouldSendReminderSettingAttributes.get(appointmentType)]
      let titleCasedAppointmentType = this.titleCase(appointment.data.appointmentType)
      let appointmentIsoDateTime = !appointment.data.isCanceled && appointment.startTimestamp ? new Date(appointment.startTimestamp).toISOString().substr(0, 10) : null
      let appointmentLocalizedDateTime = !appointment.data.isCanceled && appointment.startTimestamp ? new Date(appointment.startTimestamp).toLocaleDateString('en-US', appointmentDateTimeFormat[language]).replace(/[^ -~]/g, '') : null

      return {
        'userid': this.patient.userId,
        'attributes': {
          '3ELang': language || this.patientLang,
          [shouldSendReminderKey]: shouldSendReminderValue,
          [`3E${titleCasedAppointmentType}ApptISO`]: appointmentIsoDateTime,
          [`3E${titleCasedAppointmentType}ApptTime`]: appointmentLocalizedDateTime
        }
      }
    },
    scheduleBrazeAppointmentReminder (appointment) {
      return new Promise((resolve, reject) => {
        let proxyUrl = process.env.VUE_APP_PROXY_URL + this.userId
        let brazeAppointmentReminder = this.constructBrazeAppointmentReminder(appointment)
        let requestConfig = {
          headers: {
            ...this.requestConfig.headers,
            'request-url': process.env.VUE_APP_BRAZE_URL
          }
        }

        this.$axios.post(
          proxyUrl,
          brazeAppointmentReminder,
          requestConfig
        ).then(
          response => { resolve() }
        ).catch(
          error => {
            this.$rollbar.error('Error scheduling appointment reminder', error)
          }
        ).then(resolve)
      })
    }
  }
}
</script>
